import { useState, memo, useMemo } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import classes from "./ConfirmStatusChangeModal.module.scss";
import {
  ORDER_CONFIRMED,
  ORDER_PLACED,
  ORDER_RECEIVED,
} from "../../../constants/orderStatus";
import ColorButtons from "../ColorButtons";
import StatusDateInput from "../../order/StatusDateInput";
import { getDefaultDateInput } from "../../../functions/order/orderDateUpdate";
import {
  buildStatusChangeNotices,
  VARIANT_BULK,
} from "../../../functions/order/orderDateMessages";
import {
  canUserEditDates,
  checkBulkTransition,
  hasRollback,
} from "../../../functions/order/statusTransition";

const style = {
  box: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: {
      xs: 320, // 0px以上の幅で360pxを適用
      sm: 360, // 600px以上の幅で400pxを適用
    },
    bgcolor: "background.paper",
    border: "none",
    borderRadius: "10px",
    boxShadow: 24,
    padding: "20px 0",
    maxHeight: "90vh",
    overflowY: "auto",
  },
};

const statusClass = (status) => {
  if (status === ORDER_PLACED) return classes.placed;
  if (status === ORDER_CONFIRMED) return classes.confirmed;
  if (status === ORDER_RECEIVED) return classes.received;
  return classes.canceled;
};

// 一括のステータス変更の確認ダイアログ。
// 遷移可否の判定は functions/order/statusTransition.js に一本化してある
// (かつては一括と単票で許可する遷移が食い違っていた)。
export const ConfirmStatuChangeModal = memo((props) => {
  const { targetStatus, setIsShow, onExec, onCancel, orders, userInfo } = props;
  const [open, setOpen] = useState(true);
  const now = useMemo(() => new Date(), []);
  const [dateInput, setDateInput] = useState(() =>
    getDefaultDateInput(orders, targetStatus, now)
  );

  const [isExecuting, setIsExecuting] = useState(false);

  const handleClose = () => {
    setIsShow(false);
    setOpen(false);
  };

  const { isUpdatable, unUpdatables } = checkBulkTransition(
    targetStatus,
    orders,
    userInfo
  );
  const canEditDates = canUserEditDates(userInfo);

  // onExec は呼び出し側でダイアログを閉じるため、通常このコンポーネントは即アンマウントされる。
  // 閉じるまでの一瞬に二度押しされても更新が2回走らないようにフラグで止める。
  const handleExec = () => {
    if (isExecuting) return;
    setIsExecuting(true);
    onExec(targetStatus, orders, canEditDates ? dateInput : undefined);
  };
  const rollbackCount = (orders || []).filter((order) =>
    hasRollback(targetStatus, [order])
  ).length;
  // 表示文言は functions/order/orderDateMessages.js に集約してある
  // (単票ダイアログと同じ関数を使い、案内が食い違わないようにする)
  const {
    dateField,
    dateLabel,
    isAutoRecorded,
    autoNotice,
    hint,
    remainingDatesNotice,
  } = buildStatusChangeNotices({
    orders,
    targetStatus,
    dateInput,
    now,
    variant: VARIANT_BULK,
  });

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        // カレンダーのポップアップはPortalでbody直下(=このModalの外)に描画されるため、
        // フォーカストラップが有効だと日付を選んだ瞬間に焦点を奪い返されてしまう
        disableEnforceFocus
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style.box}>
          <div className={classes.root}>
            {isUpdatable ? (
              <div className={classes.container}>
                <div className={`${classes.title} ${classes.confirm}`}>
                  <ErrorOutlineIcon className={classes.iconImage} />
                  {`${orders.length}件の注文を`}
                  <p className={`${classes.status} ${statusClass(targetStatus)}`}>
                    {targetStatus}
                  </p>
                  にします
                </div>
                {rollbackCount > 0 && (
                  <div className={classes.rollback}>
                    {`⚠ ステータスを戻す注文が ${rollbackCount} 件含まれています`}
                  </div>
                )}
                {canEditDates && remainingDatesNotice && (
                  <div className={classes.staleNote}>
                    {remainingDatesNotice}
                  </div>
                )}
                {canEditDates && dateField && (
                  <div className={classes.dateSection}>
                    <StatusDateInput
                      label={dateLabel}
                      value={dateInput}
                      onChange={setDateInput}
                      hint={hint}
                      locked={isAutoRecorded}
                      notice={autoNotice}
                    />
                  </div>
                )}
                <div className={classes.spacer}></div>
                <div className={classes.message}>{"よろしいですか？"}</div>
                <div className={classes.buttons}>
                  <ColorButtons
                    label={"はい"}
                    color="placed"
                    onClick={handleExec}
                    // 二度押しで同じ更新が2回走らないようにする
                    disabled={isExecuting}
                    width={90}
                    height={32}
                    bottom={0}
                  />
                  <ColorButtons
                    label={"いいえ"}
                    color="grey"
                    onClick={onCancel}
                    width={90}
                    height={32}
                    bottom={0}
                  />
                </div>
              </div>
            ) : (
              <div className={classes.container}>
                <div className={`${classes.title} ${classes.alert}`}>
                  <ErrorOutlineIcon className={classes.iconImage} />
                  {`実行できない注文が${unUpdatables.length}件含まれています`}
                </div>
                <div className={classes.message}>{"注文番号"}</div>
                <div className={classes.orderNos}>
                  {unUpdatables.map((order) => (
                    <div
                      key={order.id}
                      className={`${classes.orderNo} ${statusClass(
                        order.status
                      )}`}
                    >
                      {order.order_number}
                    </div>
                  ))}
                </div>
                <ColorButtons
                  label={"閉じる"}
                  color="grey"
                  onClick={onCancel}
                  width={90}
                  height={32}
                  bottom={0}
                />
              </div>
            )}
          </div>
        </Box>
      </Modal>
    </div>
  );
});
